import get from "lodash/get";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import groupBy from "lodash/groupBy";

import { REVIEW_PASSWORD, defaultFirebaseProjectId } from "../constants";
import moment from "moment";
import {
  PROGRAMMING,
  getAthleteFromId,
  getAthleteInfo,
} from "../views/coaching-portal/tasks/task-constants";
import { setSession } from "../utils/session-helper";
import { isValidVideoLink, validURL } from "../utils/string-helpers";
import assignWorkoutToFolder from "./helpers/assign-workout-folder";
import i18n from "../i18n";
import {
  programByOrder,
  programHasNoEmptyWorkouts,
} from "../utils/copiable-cycle";

// An HTTP status means the API answered (Rill HttpError stores it on `code`).
function httpStatus(error) {
  if (!error || typeof error !== "object") return undefined;
  const candidates = [error.status, error.statusCode];
  if (typeof error.code === "number") candidates.push(error.code);
  for (const candidate of candidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric >= 100 && numeric <= 599) {
      return numeric;
    }
  }
  return undefined;
}

// Chrome: "Failed to fetch". Firefox: "NetworkError when attempting to fetch
// resource." Safari: "Load failed". Node fetch uses ECONN* codes instead.
function isFetchTypeError(error) {
  if (!error || error.name !== "TypeError") return false;
  const message = String(error.message || "");
  return (
    /failed to fetch/i.test(message) ||
    /networkerror when attempting to fetch resource/i.test(message) ||
    /^load failed$/i.test(message)
  );
}

function isNetworkCode(code) {
  return (
    typeof code === "string" &&
    (code.startsWith("ECONN") ||
      code === "ENOTFOUND" ||
      code === "ETIMEDOUT" ||
      code === "EAI_AGAIN" ||
      code === "ENETUNREACH" ||
      code === "EHOSTUNREACH" ||
      code === "EPIPE")
  );
}

function isNetworkFailure(error) {
  if (!error || typeof error !== "object") return false;
  if (isFetchTypeError(error)) return true;
  return (
    isNetworkCode(error.code) || isNetworkCode(error.cause && error.cause.code)
  );
}

const Cycle = (app) => {
  app
    .post(
      "/cycle/send/",
      async ({ api, assert, res, req, load, locals, flash }) => {
        try {
          const { body } = req;

          const { cycleId, programOrder, destinationCycleId, recipients } =
            body;

          const hasOrganizationId = !!locals.activeUser.organizationId;

          let queryLoadedCycle = {
            cycle: cycleId,
          };
          //in this step we want to capture the athlete (the only recipient) this is only for program builder
          if (recipients && recipients.length === 1 && hasOrganizationId) {
            queryLoadedCycle = {
              ...queryLoadedCycle,
              athleteFromOrganization: recipients[0],
            };
          }
          const loadedCycle = await load("editCycle", queryLoadedCycle);

          await load("editCycle", {
            cycle: destinationCycleId,
          });

          // Sending/Pushing Individual Program
          if (programOrder) {
            // Make sure, user is not sending the program to the same cycle
            assert(
              !isEqual(destinationCycleId, cycleId),
              400,
              i18n.t("dashboard:cycleActions.cannotSendToSameCycle")
            );

            // Make sure the program is not empty. Match on order: block
            // numbers can have gaps, so programs[order - 1] is the wrong block.
            assert(
              programHasNoEmptyWorkouts(
                programByOrder(loadedCycle.programs, programOrder)
              ),
              400,
              i18n.t("dashboard:cycleActions.pushedProgramsNoEmptyWorkouts")
            );
          }
          // If programOrder is undefined we send all Programs.
          else {
            // Make sure the cycle has no empty programs
            loadedCycle.programs.forEach((programItem) => {
              assert(
                programHasNoEmptyWorkouts(programItem),
                400,
                i18n.t("dashboard:cycleActions.pushedProgramsNoEmptyWorkouts")
              );
            });
          }
          // Org sub-coaches' sends must be filed under the org owner's identity
          // so they match how the coaching portal later queries a client's
          // Programs page for org accounts (see msb-api setOrganizationOwner).
          const organizationCoachQuery = hasOrganizationId
            ? `?organizationCoach=${locals.activeUser._id}`
            : "";

          await api(`cycle-send${organizationCoachQuery}`, {
            method: "POST",
            body,
          });
          flash(
            "success",
            body.programOrder
              ? i18n.t("dashboard:cycleActions.successfullySentProgram")
              : i18n.t("dashboard:cycleActions.successfullySentCycle")
          );
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          if (isNetworkFailure(error)) {
            console.error("copy network failure", error);
            res.status = 503;
            res.set("x-copy-failed", "1");
            res.body = " ";
            return;
          }
          // Validation and API errors still go to the toast. Anything else
          // is a bug: log it, and don't tell the coach it was the network.
          if (httpStatus(error) != null) throw error;
          console.error("copy failed", error);
          res.status = 500;
          res.set("x-copy-error", "1");
          res.body = " ";
        }
      }
    )

    .post(
      "/cycle/:id",
      async ({ api, assert, flash, req, res, load, locals }) => {
        const {
          query: { proxyAthlete, organizationCoach = undefined },
        } = locals;
        const { body } = req;

        const queryOrganizationCoach = organizationCoach
          ? `&organizationCoach=${organizationCoach}`
          : "";

        const hasOrganizationId = !!locals.activeUser.organizationId;
        const cycle = await load("editCycle", {
          cycle: req.params.id,
          proxyAthlete,
          athleteFromOrganization: hasOrganizationId ? proxyAthlete : undefined,
        });

        const isPremiumCoach = locals.activeUser.hasRole("coachCanSubmit");
        const isCoach = locals.activeUser.hasRole("coach");

        if (proxyAthlete) {
          // Check if the athlete is inactive and prohibit programs if so
          const athlete = find(
            locals.activeUser.athletes,
            (athlete) => String(athlete._id) === String(proxyAthlete)
          );
          const athleteInactive =
            athlete && athlete.status && athlete.status.inactive;
          assert(
            !athleteInactive,
            400,
            i18n.t("dashboard:cycleActions.cantCreateBlocksForInactive")
          );
        }

        assert(
          cycle,
          400,
          i18n.t("dashboard:cycleActions.programDoesNotExist")
        );
        const program = req.body || {};
        let redirectPath, flashMessage;

        if (program.source == "review") {
          // Submit a cycle/program for review
          assert(
            program.password === REVIEW_PASSWORD,
            400,
            i18n.t("dashboard:cycleActions.wrongAdminPassword")
          );

          assert(
            cycle.programs.length &&
              cycle.programs.some((p) => p.status !== "published"),
            400,
            i18n.t("dashboard:cycleActions.noBlockToApprove")
          );
          cycle.status = "published";
          cycle.programs = cycle.programs.map((program) => {
            program.status = "published";
            return program;
          });
          if (cycle.type !== "standard") cycle.type = "premium";

          cycle.createdDate = Date.now();

          redirectPath = `/dashboard/program-library/program-overview/${cycle._id}?program=${program.order}`;
          flashMessage = i18n.t("dashboard:cycleActions.successfullyPublished");
        } else {
          // asserting required fields are present
          // common asserts

          // An athlete can't add programs to a coach-made cycle
          assert(
            !(cycle.type === "coach" && !isCoach),
            400,
            i18n.t("dashboard:cycleActions.blocksCantBeAddedToCoachPrograms")
          );

          // coach asserts
          if (isPremiumCoach && cycle.type !== "coach") {
            if (cycle.programs && cycle.storeType !== "teams")
              assert(
                cycle.programs.length <= 4,
                400,
                i18n.t("dashboard:cycleActions.maximumFourBlocksPerProgram")
              );
          }

          program.modifiedDate = Date.now();
          program.createdDate = program.createdDate || Date.now();
          program.exercises = [];

          if (!cycle.programs) cycle.programs = [];
          cycle.programs.push({
            name: `Block ${cycle.programs.length + 1}`,
            numberOfWeeks: cycle.numberOfWeeks || 4,
            sessionsPerWeek: cycle.sessionsPerWeek || 1,
            description:
              cycle.storeType === "teams" ? null : "Some description...",
            modifiedDate: Date.now(),
            createdDate: Date.now(),
            exercises: [],
            ...(body.startDate ? { startDate: Number(body.startDate) } : {}), // The startDate is already being sent as a number but who knows with rill o json
            ...(body.firstDayOfWeek
              ? { firstDayOfWeek: Number(body.firstDayOfWeek) }
              : {}),
          });

          redirectPath = proxyAthlete
            ? // then its made for a particular athlete
              `/athlete-portal/programs/edit-program/${cycle._id}?athlete=${proxyAthlete}&program=${cycle.programs.length}${queryOrganizationCoach}`
            : cycle.type === "coach" && cycle.status !== "pushed"
              ? // then its made as a template
                `/coaching-portal/templates/edit-program/${cycle._id}?program=${cycle.programs.length}`
              : cycle.type === "subscription" &&
                  cycle.status !== "published" &&
                  cycle.storeType !== "teams"
                ? // then its made as a template but from store section
                  `/coaching-portal/store/edit-program/${cycle._id}?program=${cycle.programs.length}`
                : cycle.storeType === "teams"
                  ? // It is a team and therefore it should go to the team builder cmon man what else do you want from me, I'm just a comment
                    `/coaching-portal/store/edit-program/${cycle._id}?program=${cycle.programs.length}&storeType=teams`
                  : // then it should go to dashboard
                    `/dashboard/program-builder/edit-program/${cycle._id}?program=${cycle.programs.length}`;
          flashMessage = i18n.t(
            "dashboard:cycleActions.successfullyConfiguredBlock"
          );
        }

        try {
          const updatedcycle = await api(
            `cycle/${cycle._id}/?proxyAthlete=${proxyAthlete}`,
            {
              method: "PUT",
              body: {
                programs: cycle.programs,
                status: cycle.status,
                modifiedDate: Date.now(),
              },
            }
          );
          const cycleJson = await updatedcycle.json();
          locals.editCycle = cycleJson;
          flash("success", flashMessage);
        } catch (e) {
          console.log("error in program/:id", e);
          flash("error", e.message);
        }

        res.redirect(redirectPath);
      }
    )
    .post("/cycle", async ({ api, assert, res, req, load, locals, flash }) => {
      let { body, files, query = {} } = req,
        flashMessage;
      const {
        isTemplate,
        proxyAthlete,
        isSubscription,
        organizationCoach = undefined,
        storeType,
        fromTemplate,
      } = query;

      console.log("fromTemplate", fromTemplate);

      if (!body._id) {
        if (storeType) {
          body.storeType = storeType;

          // Make sure the single-workout has only 1 of each
          if (storeType === "single-workout" || storeType === "teams") {
            body.numberOfWeeks = 1;
          }
        }

        // need active cycles to check name uniqueness
        if (proxyAthlete || isTemplate || isSubscription) {
          // Check if the athlete is inactive and prohibit programs if so
          const athlete = find(
            locals.activeUser.athletes,
            (athlete) => athlete._id === proxyAthlete
          );
          const athleteInactive =
            athlete && athlete.status && athlete.status.inactive;
          assert(
            !athleteInactive,
            400,
            i18n.t("dashboard:cycleActions.cantCreateBlocksForInactive")
          );

          body.type = isSubscription ? "subscription" : "coach";
          body.owner = proxyAthlete;
        }

        // asserting required fields are present
        // @format => assert(condition, STATUS_CODE, message)
        // asserts if condition is false
        if (!body.name) {
          flash(
            "error",
            i18n.t("dashboard:cycleActions.programNameCannotBeBlank")
          );
          return res.refreshWith({ submitError: true }, { url: "back" });
        }
        // setting defaults
        const tags = JSON.parse(body.tags);
        if (tags.length > 8) {
          flash(
            "error",
            i18n.t("dashboard:cycleActions.maximumEightTagsAllowed")
          );
          return res.refreshWith({ submitError: true }, { url: "back" });
        }
        body.status = body.status || (isSubscription ? "draft" : "created");
        // difficulty
        body.difficulty = {
          1: "Beginner",
          2: "Intermediate",
          3: "Expert",
        }[body.difficulty];
        body.type =
          body.type ||
          {
            admin: "standard",
            coachCanSubmit: "premium",
            ambassador: "user",
            coach: "user", // if body.type is not already set to 'coach' above, then the coach is just creating a cycle as a user
            user: "user",
          }[locals.activeUser.leadRole];

        body.createdBy = locals.activeUserToRequest._id;
        body.owner = body.owner || body.createdBy;
        body.goals = tags;
        if (body.trainingSplitType !== "forced") {
          body.trainingSplitType = "suggested";
        }

        // Make sure limit tag 8
        body.programs = [
          {
            name: "Block 1",
            goals: "",
            numberOfWeeks: body.numberOfWeeks,
            sessionsPerWeek: body.sessionsPerWeek,
            description: "",
            squatFrequency: 0,
            benchFrequency: 0,
            deadliftFrequency: 0,
            isEquippedLifting: true,
            modifiedDate: Date.now(),
            createdDate: Date.now(),
            exercises: [],
            trainingSplitType: body.trainingSplitType,
            ...(body.startDate
              ? { startDate: Number(moment(body.startDate).format("YYYYMMDD")) }
              : {}),
            ...(body.firstDayOfWeek
              ? { firstDayOfWeek: Number(body.firstDayOfWeek) }
              : {}),
          },
        ];

        if (body.video) {
          if (!isValidVideoLink(body.video)) {
            flash("error", i18n.t("dashboard:cycleActions.videoUrlNotValid"));
            return res.refreshWith({ submitError: true }, { url: "back" });
          }
        }

        if (body.startDate) {
          body.startDate = Number(moment(body.startDate).format("YYYYMMDD"));
        }

        // Captured before `body` is reassigned to the API response below,
        // which has no folderId field of its own.
        const folderId = query.folderId || body.folderId;

        body = await (
          await api("cycle", {
            method: "POST",
            body,
            files,
            proxyAthlete,
          })
        ).json();

        // If created inside (or assigned to) a workout folder, attach the new workout to it
        if (
          folderId &&
          body._id &&
          (storeType === "single-workout" || body.storeType === "single-workout")
        ) {
          try {
            await assignWorkoutToFolder({
              api,
              coachId: locals.activeUserToRequest._id,
              workoutId: body._id,
              folderId,
            });
          } catch (e) {
            console.log("error assigning workout to folder", e);
          }
        }

        flashMessage = i18n.t("dashboard:cycleActions.successfullyCreated");
      } else {
        flashMessage = i18n.t("dashboard:cycleActions.successfullySelected");
      }

      let organizationQuery = ``;

      if (organizationCoach) {
        organizationQuery = `&organizationCoach=${organizationCoach}`;
      }

      const redirectPath = proxyAthlete
        ? // then its made for a particular athlete
          `/athlete-portal/programs/edit-program/${body._id}?athlete=${proxyAthlete}&program=1` +
          organizationQuery
        : fromTemplate === true || fromTemplate === "true"
          ? // then its made from templates section (e.g. teams programs)
            `/coaching-portal/templates/edit-program/${body._id}?program=1`
          : body.type === "coach" && body.status !== "pushed"
            ? // then its made as a template
              `/coaching-portal/templates/edit-program/${body._id}?program=1`
            : body.type === "subscription" && body.status !== "published"
              ? // then its made as a template but from store section
                `/coaching-portal/store/edit-program/${body._id}?program=1`
              : // then it should go to dashboard
                `/dashboard/program-builder/edit-program/${body._id}?program=1`;

      flash("success", flashMessage);
      res.redirect(`${redirectPath}&submitError=false`);
    })

    .post(
      "/cycle/edit/:id",
      // Edit a cycle from the edit-cycle-settings modal
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        let {
          body,
          files,
          query: { athlete, coaching },
        } = req;
        assert(
          body.name,
          400,
          i18n.t("dashboard:cycleActions.programNameRequired")
        );

        // Capture the previous trainingSplitType so we can tell whether the
        // coach flipped the toggle and needs future weeks re-stamped.
        let previousTrainingSplitType;
        if (body.trainingSplitType) {
          const previousCycle = await load("editCycle", {
            cycle: req.params.id,
          });
          previousTrainingSplitType =
            previousCycle?.trainingSplitType || "suggested";
        }

        if (
          ["premium", "standard"].includes(body.type) &&
          body.status === "published"
        ) {
          const cycle = await load("editCycle", { cycle: req.params.id });
          cycle.programs = cycle.programs.map((program) => {
            program.status = "published";
            return program;
          });
          body = Object.assign({}, cycle, body);
        }

        body.goals = JSON.parse(body.tags);
        if (!body.goals.length) {
          body.goals = "clean_tags";
        }
        body.difficulty = {
          1: "Beginner",
          2: "Intermediate",
          3: "Expert",
        }[body.difficulty];

        if (body.video) {
          assert(
            isValidVideoLink(body.video),
            400,
            i18n.t("dashboard:cycleActions.videoUrlNotValid")
          );
        } else if (body.video === "") {
          // cast() in RillFetcher drops empty strings, so an emptied video
          // would never reach the API and the old link would persist. Send a
          // sentinel (mirrors the `clean_tags` pattern above) so the backend
          // knows to clear it.
          body.video = "clear_video";
        }

        try {
          const response = await api(`cycle/${req.body._id}`, {
            method: "PUT",
            body,
            files,
          });
          let editCycle = await response.json();

          if (
            body.trainingSplitType &&
            body.trainingSplitType !== previousTrainingSplitType
          ) {
            try {
              const splitResponse = await api("cycle-teams-training-split", {
                method: "PUT",
                body: {
                  cycleId: req.params.id,
                  trainingSplitType: body.trainingSplitType,
                },
              });
              const splitData = await splitResponse.json();
              // Prefer the propagated cycle so the session cache reflects the
              // new trainingSplitType on both the cycle and its future programs.
              if (splitData?.cycle) {
                editCycle = splitData.cycle;
              }
            } catch (splitErr) {
              console.log("Error propagating training split", splitErr);
            }
          }

          setSession(session, "editCycle", editCycle, "2 seconds");

          // Reflect any folder change made from the edit form
          if (editCycle?.storeType === "single-workout") {
            try {
              await assignWorkoutToFolder({
                api,
                coachId: locals.activeUserToRequest._id,
                workoutId: req.params.id,
                folderId: body.folderId,
              });
            } catch (folderErr) {
              console.log("error assigning workout to folder", folderErr);
            }
          }
        } catch (e) {
          console.log("Error", e);
        }
        flash("success", i18n.t("dashboard:cycleActions.successfullyUpdated"));

        if (
          athlete ||
          (body.type === "coach" && body.status !== "pushed") ||
          coaching
        )
          res.refreshWith({ modal: "" }, { url: "back" });
        else res.redirect(`/dashboard/program-builder`);
      }
    )
    .get(
      "/cycle/unpublish/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        const store = req.query.store || false;
        assert(id, 400, i18n.t("dashboard:cycleActions.programIdRequired"));

        if (
          process.browser &&
          confirm(i18n.t("dashboard:cycleActions.confirmUnpublishProgram"))
        ) {
          try {
            const updatedcycle = await api(`cycle-unpublish`, {
              method: "PUT",
              body: { id, store },
            });
          } catch (e) {
            console.log("Error", e);
          }
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyUnpublished")
          );
          if (store) {
            res.refreshWith({ status: "draft" }, { url: "back" });
          } else {
            res.redirect(`/dashboard/program-builder`);
          }
        } else {
          res.refreshWith({ status: "published" }, { url: "back" });
        }
      }
    )
    .get(
      "/cycle/publish/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        assert(id, 400, i18n.t("dashboard:cycleActions.programIdRequired"));

        if (
          process.browser &&
          confirm(i18n.t("dashboard:cycleActions.confirmPublishProgram"))
        ) {
          try {
            const updatedcycle = await api(`cycle-publish`, {
              method: "PUT",
              body: { id },
            });
            flash(
              "success",
              i18n.t("dashboard:cycleActions.successfullyPublished")
            );
            res.refreshWith({ status: "published" }, { url: "back" });
          } catch (e) {
            flash("error", e.message);
            res.refreshWith({ status: "draft" }, { url: "back" });
          }
        } else {
          res.refreshWith({ status: "draft" }, { url: "back" });
        }
        // res.redirect(`/coaching-portal/store`);
      }
    )
    .get(
      "/cycle/store/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        assert(id, 400, i18n.t("dashboard:cycleActions.programIdRequired"));

        try {
          const updatedcycle = await api(`cycle-store`, {
            method: "POST",
            body: { id, user: locals.activeUserToRequest._id },
          });
        } catch (e) {
          console.log("Error", e);
        }
        flash(
          "success",
          i18n.t("dashboard:cycleActions.successfullyCopiedToStore")
        );
        res.redirect(`/coaching-portal/store?status=draft`);
      }
    )
    .post(
      "/cycle/edit/program/:cycleId",
      // Edit a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        let body = req.body;
        const cycleId = locals.params.cycleId;

        let queryEditCycle = {
          cycle: cycleId,
          proxyAthlete: req.query.athlete,
        };

        if (req.query.athlete && locals.activeUser.organizationId) {
          queryEditCycle = {
            ...queryEditCycle,
            athleteFromOrganization: req.query.athlete,
          };
        }

        await load("editCycle", queryEditCycle);

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - EDIT PROGRAM BEFORE",
            "6906fa7a2467801a6168cb99",
            {
              program: locals.editCycle.programs[locals.query.program - 1],
              body: req.body,
            }
          );
        }

        locals.editCycle.programs[locals.query.program - 1] = Object.assign(
          locals.editCycle.programs[locals.query.program - 1],
          req.body,
          { modifiedDate: Date.now() }
        );

        locals.editCycle.programs.forEach((program) => {
          if ("undefined" in program) delete program["undefined"];
        });

        if (body.video) {
          assert(
            isValidVideoLink(body.video),
            400,
            i18n.t("dashboard:cycleActions.videoMustBeYoutubeOrLoom")
          );
        }

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - EDIT PROGRAM AFTER",
            "6906fa7a2467801a6168cb99",
            {
              program: locals.editCycle.programs,
            }
          );
        }

        try {
          const response = await api(`cycle/${cycleId}`, {
            method: "PUT",
            body: {
              programs: locals.editCycle.programs,
              modifiedDate: Date.now(),
            },
          });
          const cycle = await response.json();
          setSession(session, "editCycle", cycle);
        } catch (e) {
          console.log("Error: ", e);
        }
        flash("success", i18n.t("dashboard:cycleActions.successfullyUpdated"));
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .get(
      "/cycle/duplicate/:cycleId",
      // Duplicate a cycle
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const cycle = locals.params.cycleId;
        const store = req.query.store || false;
        const user = locals.activeUserToRequest._id;

        await api("cycle-duplicate-cycle", {
          method: "POST",
          body: { cycle, store, user },
        });
        flash("success", i18n.t("dashboard:cycleActions.programDuplicated"));
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/program/duplicate/:cycleId",
      // Duplicate a program within a cycle
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { cycleId } = locals.params;

        const program = locals.query.program || undefined;
        const user = locals.activeUserToRequest._id;
        assert(cycleId, 400, i18n.t("dashboard:cycleActions.programRequired"));
        assert(program, 400, i18n.t("dashboard:cycleActions.blockRequired"));
        delete program.progress;
        delete program.instances;

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - DUPLICATE PROGRAM",
            "6906fa7a2467801a6168cb99",
            {
              program,
            }
          );
        }

        const updatedcycle = await api(`cycle-duplicate-program`, {
          method: "POST",
          body: { program, cycle: cycleId, user },
        });
        flash("success", i18n.t("dashboard:cycleActions.blockDuplicated"));
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/programs/reorder/:cycleId/:from/:to",
      async ({ req, res, locals, api, load, assert, session }) => {
        const { from, to, cycle } = req.params;

        await api(`cycle-program-order`, { method: "POST", body: req.params });

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/like/:cycleId",
      // Like or unlike a cycle
      async ({ api, assert, res, req, load, locals }) => {
        const { params } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );

        await api(`like-cycle`, {
          method: "PUT",
          body: {
            cycle: params.cycleId,
            user: locals.activeUser._id,
          },
        });

        // TODO: this redirect will have to be different on the library page
        res.backDontScroll();
        // res.redirect(`/dashboard/program-library/program-overview/${params.cycleId}`);
      }
    )
    .get(
      "/cycle/addWorkout/:cycleId",
      // Add an empty workout to a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );

        if (params.cycleId === "6906fa7a2467801a6168cb99") {
          console.log("LUISLOG - ADD WORKOUT", "6906fa7a2467801a6168cb99", {
            cycle: params.cycleId,
            program: query.program,
            week: query.week,
          });
        }

        const response = await api(`cycle-add-workout`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: query.program,
            week: query.week,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);

        flash(
          "success",
          query.source === "addWeek"
            ? i18n.t("dashboard:cycleActions.newWeekAdded")
            : i18n.t("dashboard:cycleActions.workoutAdded")
        );
        res.refreshWith({ week: query.week }, { url: "back" });
      }
    )
    .post(
      "/cycle/program/addWeek",
      async ({ api, assert, res, req, flash, locals, momentTZ, session }) => {
        const { cycle, program, sessionsPerWeek, initNewWeek, newWeeks } =
          req.body;
        const { activeUserToRequest } = locals;
        assert(
          cycle?._id && cycle.programs,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );
        const createdBy = cycle.createdBy?._id || cycle.createdBy;
        const owner = cycle.owner?._id || cycle.owner;
        assert(
          createdBy == activeUserToRequest._id ||
            owner == activeUserToRequest._id,
          400,
          i18n.t("dashboard:cycleActions.cannotUpdateAnotherUserProgram")
        );

        const sessionsToAdd = sessionsPerWeek || 1;
        const programs = [...cycle.programs];
        const index = programs.findIndex(({ order }) => order == program);
        assert(
          index !== -1,
          400,
          i18n.t("dashboard:cycleActions.blockDoesNotExist")
        );
        const week = initNewWeek + newWeeks;
        const startingWeekToIterate = initNewWeek + 1;
        for (let w = 0; w < newWeeks; w++) {
          const currentWeek = startingWeekToIterate + w;
          let maxSession = 0;
          for (let i = 0; i < sessionsToAdd; i++) {
            // get the last session that exists
            const lastSession = programs[index].sessions
              .filter((session) => session.week == currentWeek)
              .reduce(
                (prev, current) =>
                  prev.order > current.order ? prev : current,
                { order: 0 }
              );
            maxSession = Number(lastSession.order);
            // Prohibit more workouts than days in a week
            assert(
              maxSession < 7,
              400,
              i18n.t(
                "dashboard:cycleActions.cannotAddMoreThanSevenWorkoutsPerWeek"
              )
            );

            // add the session to the program
            programs[index].sessions.push({
              week: currentWeek,
              date: momentTZ(0).add(currentWeek, "weeks").format(),
              order: maxSession + 1,
              exercises: [],
            });
          }
        }

        programs[index].modifiedDate = Date.now();
        const response = await api(`cycle/${cycle._id}`, {
          method: "PUT",
          body: {
            programs,
            modifiedDate: Date.now(),
          },
        });
        const message = newWeeks > 1 ? "weeks" : "week";
        const updatedCycle = await response.json();
        setSession(session, "editCycle", updatedCycle);
        flash(
          "success",
          message === "weeks"
            ? i18n.t("dashboard:cycleActions.newWeeksAdded")
            : i18n.t("dashboard:cycleActions.newWeekAdded")
        );
        res.refreshWith({ week }, { url: "back" });
      }
    )
    .post(
      "/cycle/program/duplicateWeek",
      // Copy the sessions from one week to an empty week
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        const { body } = req;
        assert(
          get(body, "to.length"),
          400,
          i18n.t("dashboard:cycleActions.selectPlaceToDuplicateTo")
        );
        // if body.error is set, means that weeks are not valid
        assert(!body.error, 400, body.error);

        const response = await api(
          body.calendarMode
            ? `copy-week?proxyAthlete=${body.proxyAthlete}`
            : `cycle-duplicate-week`,
          { method: "POST", body }
        );
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        flash(
          "success",
          i18n.t("dashboard:cycleActions.successfullyDuplicated")
        );
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/cycle/program/duplicateWorkout",
      // Copy a workout to the same week
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        const { body } = req;
        assert(
          get(body, "to.length"),
          400,
          i18n.t("dashboard:cycleActions.selectPlaceToDuplicateTo")
        );
        const response = await api(`cycle-duplicate-workout`, {
          method: "POST",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash(
          "success",
          i18n.t("dashboard:cycleActions.successfullyDuplicated")
        );
        res.refreshWith(
          { modal: "", isExercise: false },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/store/reorder",
      // Reorder Position in Store
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        let { body } = req;
        assert(
          body.orderedCycles,
          400,
          i18n.t("dashboard:cycleActions.provideOrderOfCycles")
        );

        if (
          locals.activeUser.organizationId &&
          locals.activeUserToRequest &&
          String(locals.activeUser._id) !==
            String(locals.activeUserToRequest._id)
        ) {
          body = {
            ...body,
            userOrganizationId: locals.activeUserToRequest._id,
          };
        }

        await api(`cycle-store-reorder`, {
          method: "PUT",
          body,
        });

        // TODO: Fix weird behavior when refreshing page that causes programs disordering again but only in the DOM (not in the API)
        // flash("success", "Successfully reordered");
        // res.backDontScroll();
      }
    )
    .get(
      "/cycle/reorder-workout/:cycleId/:program/:week/:sessionOrder/:to",
      // In Editor, reorder a workout up or down by one
      async ({ api, assert, res, req, load, locals, session }) => {
        const { params, query } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );

        if (params.cycleId === "6906fa7a2467801a6168cb99") {
          console.log("LUISLOG - MOVE WORKOUT", "6906fa7a2467801a6168cb99", {
            cycle: params.cycleId,
            program: params.program,
            week: params.week,
            session: params.sessionOrder,
            to: params.to,
          });
        }

        const response = await api(`cycle-session-order`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: params.program,
            week: params.week,
            session: params.sessionOrder,
            to: params.to,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-workout/:cycleId/:program/:week/:order",
      // In Editor, delete a workout
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );
        if (
          process.browser &&
          !confirm(i18n.t("dashboard:cycleActions.confirmDeleteWorkout"))
        ) {
          return res.backDontScroll();
        }

        if (params.cycleId === "6906fa7a2467801a6168cb99") {
          console.log("LUISLOG - DELETE WORKOUT", "6906fa7a2467801a6168cb99", {
            program: params.program,
            week: params.week,
            session: params.order,
          });
        }

        const response = await api(`cycle-delete-workout`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: params.program,
            week: params.week,
            session: params.order,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        flash("success", i18n.t("dashboard:cycleActions.workoutDeleted"));

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-week/:cycleId/:program/:week",
      // In Editor, delete a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );
        if (
          process.browser &&
          !confirm(
            i18n.t("dashboard:cycleActions.confirmDeleteEntireWeek", {
              week: params.week,
            })
          )
        ) {
          return res.backDontScroll();
        }
        try {
          const response = await api(`cycle-delete-week`, {
            method: "POST",
            body: {
              cycle: params.cycleId,
              program: params.program,
              week: params.week,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", i18n.t("dashboard:cycleActions.weekDeleted"));
        } catch (err) {
          flash("error", i18n.t("dashboard:cycleActions.couldNotDeleteWeek"));
        }
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-single-workout/:cycleId/",
      // In Editor, delete a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );
        if (
          process.browser &&
          !confirm(i18n.t("dashboard:cycleActions.confirmClearEntireWorkout"))
        ) {
          return res.backDontScroll();
        }
        try {
          const response = await api(`cycle-delete-single-workout`, {
            method: "POST",
            body: {
              cycle: params.cycleId,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", i18n.t("dashboard:cycleActions.workoutDeleted"));
        } catch (err) {
          flash(
            "error",
            i18n.t("dashboard:cycleActions.couldNotDeleteWorkout")
          );
        }
        res.backDontScroll();
      }
    )
    .post(
      "/cycle/program/delete-weeks",
      // In Editor, delete multiple weeks
      async ({ api, assert, res, req, locals, flash, session }) => {
        const { body } = req;
        const { cycle, program, selectedWeeks } = body;
        assert(cycle, 400, i18n.t("dashboard:cycleActions.programRequired"));
        assert(program, 400, i18n.t("dashboard:cycleActions.blockRequired"));
        assert(
          selectedWeeks?.length,
          400,
          i18n.t("dashboard:cycleActions.weeksRequired")
        );

        try {
          const response = await api("cycle-delete-weeks", {
            method: "POST",
            body: {
              cycle,
              program,
              weeks: selectedWeeks,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", i18n.t("dashboard:cycleActions.weeksDeleted"));
        } catch (err) {
          flash("error", i18n.t("dashboard:cycleActions.couldNotDeleteWeeks"));
        }
        res.backDontScroll();
      }
    )
    .post(
      "/cycle/add-to-calendar/:cycleId",
      // From the add-to-calender modal, schedule all the sessions in an array of programs
      async ({ api, assert, res, req, load, locals, flash }) => {
        const { params, body } = req;
        assert(
          params.cycleId,
          400,
          i18n.t("dashboard:cycleActions.programRequired")
        );
        // Filter out invalid programs, then get rid of extraneous info
        const programs = body.formPrograms
          .filter((program) => {
            if (program.isValid === "true") {
              // Check that all the workouts have at least one exercise
              program.sessions.forEach((session) => {
                assert(
                  get(session, "exercises", []).length,
                  400,
                  i18n.t("dashboard:cycleActions.blockNoEmptyWorkouts")
                );
              });
              return true;
            }
          })
          .map((program) => {
            return {
              program: program.order,
              startDate: program.startDate,
              dates: program.dates,
            };
          });
        assert(
          programs.length,
          400,
          i18n.t("dashboard:cycleActions.blockRequired")
        );

        await api(`cycle-add-to-calendar`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            user: locals.activeUser._id,
            programs,
            daysToBeOverwritten: body.daysToBeOverwritten,
          },
        })
          .then((response) => {
            const reportToMobileApp = () => {
              //close window in mobile app - start
              const message = {
                type: "addToCalendarSuccess",
                message: "close",
              };
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify(message));
              }

              //close window in mobile app - end
            };

            response.status < 400 &&
              flash(
                "success",
                programs.length > 1
                  ? i18n.t(
                      "dashboard:cycleActions.programsSuccessfullyScheduled"
                    )
                  : i18n.t(
                      "dashboard:cycleActions.programSuccessfullyScheduled"
                    )
              );

            response.status < 400 && reportToMobileApp();

            response.status > 399 && flash("error", response.statusText);
          })
          .catch((err) => {
            flash("error", err.message);
          });
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .get(
      "/cycle/coach-add-session/:cycleId/:programNumber/:numberOfWeeks",
      // From the program editor footer, add a workout to each week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycleId, programNumber, numberOfWeeks },
        } = req;

        for (let i = 1; i < parseInt(numberOfWeeks) + 1; i++) {
          await api(`cycle-add-workout`, {
            method: "POST",
            body: {
              program: programNumber,
              week: i,
              cycle: cycleId,
              user: locals.activeUserToRequest._id,
            },
          });
        }

        flash(
          "success",
          i18n.t("dashboard:cycleActions.workoutAddedToEachWeek")
        );
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/coach-remove-session/:cycleId/:programNumber/:sessionNumber/:numberOfWeeks",
      // From the program editor footer, delete a workout from each week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycleId, programNumber, sessionNumber, numberOfWeeks },
        } = req;

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - DELETE WORKOUT",
            "6906fa7a2467801a6168cb99",
            cycleId,
            programNumber,
            sessionNumber,
            numberOfWeeks
          );
        }

        for (let i = 1; i < parseInt(numberOfWeeks) + 1; i++) {
          await api(`cycle-delete-workout`, {
            method: "POST",
            body: {
              program: programNumber,
              week: i,
              cycle: cycleId,
              session: sessionNumber,
              user: locals.activeUserToRequest._id,
            },
          });
        }

        flash(
          "success",
          i18n.t("dashboard:cycleActions.workoutDeletedFromEachWeek")
        );

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/coach-reorder-week/:cycleId/:programNumber/:from/:to",
      // From the program editor footer, move a week back or forth in order
      async ({ api, assert, res, req, load, locals, session }) => {
        const {
          params: { cycleId, programNumber, from, to },
        } = req;

        await api("cycle-move-week", {
          method: "POST",
          body: {
            cycle: cycleId,
            program: programNumber,
            from,
            to,
            user: locals.activeUserToRequest._id,
          },
        });

        res.backDontScroll();
      }
    )
    // .get("/cycle/coach-duplicate-week/:cycleId/:programNumber/:week",
    //   // From the program editor footer, copy a week and add it to the end of a program
    //   async ({ api, assert, res, req, load, locals }) => {
    //     const { params : {cycleId, programNumber, week}} = req;
    //
    //     await api('cycle-duplicate-week', { method: "POST", body: {
    //       cycle: cycleId,
    //       program: programNumber,
    //       week
    //     }})
    //
    //     res.backDontScroll();
    //   }
    // )
    .get(
      "/cycle/delete-cycle/:cycle",
      // From the program editor footer, copy a week and add it to the end of a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycle },
        } = req;

        if (
          process.browser &&
          confirm(i18n.t("dashboard:cycleActions.confirmDeleteProgram"))
        ) {
          const user = locals.activeUserToRequest._id;
          await api("cycle-delete", { method: "POST", body: { cycle, user } });

          flash("success", i18n.t("dashboard:cycleActions.programDeleted"));
        }

        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .get(
      "/cycle/delete-program/:cycle/:program",
      // From the program editor footer, copy a week and add it to the end of a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycle, program },
          query: { isFromCoachingPortal, isFromTeams = false },
        } = req;
        if (
          process.browser &&
          confirm(
            isFromTeams
              ? i18n.t("dashboard:cycleActions.confirmDeleteWeekFromLibrary")
              : i18n.t("dashboard:cycleActions.confirmDeleteBlockFromLibrary")
          )
        ) {
          await api("cycle-delete-program", {
            method: "POST",
            body: {
              cycle,
              program,
              isFromCoachingPortal,
            },
          });

          flash("success", i18n.t("dashboard:cycleActions.blockDeleted"));
        }
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/cycle/push/:cycle/:program/:athlete",
      // From the athlete's portal, push a program so the athlete can see it
      async ({ api, assert, res, req, load, locals, flash }) => {
        const {
          params: { cycle, program, athlete },
          body,
        } = req;
        const { activeUser, activeUserToRequest } = locals;
        const hasOrganizationId = !!locals.activeUser.organizationId;

        // Sending/Pushing Cycle

        let queryLoadedCycle = {
          cycle: req.params.cycle,
        };
        //in this step we want to capture the athlete (the only recipient) this is only for program builder
        if (athlete && hasOrganizationId) {
          queryLoadedCycle = {
            ...queryLoadedCycle,
            athleteFromOrganization: athlete,
          };
        }

        const loadedCycle = await load("editCycle", queryLoadedCycle);
        // Sending/Pushing Individual Program
        if (program) {
          loadedCycle.programs[program - 1].sessions.map((sessionItem) => {
            assert(
              get(sessionItem, "exercises", []).length,
              400,
              i18n.t("dashboard:cycleActions.pushedBlocksNoEmptyWorkouts")
            );
          });
        }
        // If program is undefined we send all programs.
        else {
          loadedCycle.programs.map((programItem) => {
            programItem.sessions.map((sessionItem) => {
              assert(
                get(sessionItem, "exercises", []).length,
                400,
                i18n.t("dashboard:cycleActions.pushedBlocksNoEmptyWorkouts")
              );
            });
          });
        }

        try {
          // CH-4085
          await (await api(`check-valid-cycle?id=${cycle}`)).json();
          await api("cycle-push", {
            method: "POST",
            body: {
              cycle,
              program,
              athlete,
              avatar:
                !!activeUser.avatar && !!activeUser.avatar.file
                  ? activeUser.avatar.file
                  : null,
              firebaseProjectId: !!activeUserToRequest.firebaseProjectId
                ? activeUserToRequest.firebaseProjectId
                : defaultFirebaseProjectId,
            },
          });
        } catch (e) {
          console.log("caught in fetch", e.message);
          throw e;
        }

        flash(
          "success",
          i18n.t("dashboard:cycleActions.successfullyPushedBlock")
        );

        try {
          const task = {
            type: PROGRAMMING,
            completed: false,
            date: moment.utc(body.date).valueOf(),
          };
          //find the athlete in the coach of the organization, or in the active user
          const athleteForTask = body.athleteForTask;
          if (!athleteForTask) {
            throw new Error("User not found. Cannot create task");
          }
          task.athlete = athleteForTask._id;
          task.athleteInfo = getAthleteInfo(athleteForTask);
          task.description = i18n.t(
            "dashboard:cycleActions.newBlockRequiredForAthlete",
            { name: athleteForTask.name }
          );
          task.cycle = loadedCycle._id;
          if (program) {
            task.program = program;
            const fullProgram = loadedCycle.programs[program - 1];
            const weeks = groupBy(fullProgram.sessions, "week");
            task.programInfo = {
              _id: fullProgram._id,
              name: fullProgram.name,
              numberOfWeeks: Object.keys(weeks).length,
            };
          }

          await api("tasks", { method: "POST", body: task });
        } catch (e) {
          // The push itself already succeeded (it would have thrown above
          // otherwise) and the success flash was already shown. Flashing an
          // "error" here clobbers it into a red toast that still reads
          // "Successfully pushed block" (page.js renders error-type styling
          // with the success message text). Log only; don't re-flash.
          console.log("caught in fetch", e.message);
        }

        res.refreshWith(
          { modal: "", pushedCycle: cycle, pushedProgram: program },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/store/manage-category",
      // Manage Category (Add/Edit)
      async ({ api, assert, flash, res, req, locals }) => {
        let { body } = req;
        assert(
          body.categoryName,
          400,
          i18n.t("dashboard:cycleActions.provideCategoryName")
        );

        const { categoryName, categoryIndex, newCategory } = body;

        const programCategories =
          locals.activeUserToRequest?.pushAccountInfo?.programCategories || [];

        const normalized = categoryName.trim().toLowerCase();
        const duplicate = programCategories.some((c, i) => {
          if (!newCategory && i === categoryIndex) return false;
          return c.trim().toLowerCase() === normalized;
        });
        assert(
          !duplicate,
          400,
          i18n.t("dashboard:cycleActions.duplicateCategory")
        );

        if (!!locals.activeUser.organizationId) {
          body = {
            ...body,
            organizationUserId: locals.activeUserToRequest._id,
          };
        }

        await api(`cycle-categories`, {
          method: "PUT",
          body,
        });

        flash("success", i18n.t("dashboard:cycleActions.successfullySaved"));
        res.refreshWith(
          {
            status: "published",
            "modal[type]": "",
            update: true,
            selectedCategoryIndex: categoryIndex,
            category: categoryName,
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    .get(
      "/cycle/store/manage-category/:categoryIndex",
      // Manage Category (Delete)
      async ({ api, assert, flash, res, req, locals }) => {
        const {
          params: { categoryIndex },
        } = req;
        assert(
          categoryIndex,
          400,
          i18n.t("dashboard:cycleActions.provideCategoryToDelete")
        );

        let queryToDelete = `cycle-categories?categoryIndex=${categoryIndex}`;

        if (
          locals.activeUser.organizationId &&
          locals.activeUserToRequest &&
          String(locals.activeUser._id) !==
            String(locals.activeUserToRequest._id)
        ) {
          queryToDelete =
            queryToDelete +
            `&organizationCoachId=${locals.activeUserToRequest._id}`;
        }

        await api(queryToDelete, {
          method: "DELETE",
        });

        flash("success", i18n.t("dashboard:cycleActions.successfullyDeleted"));

        res.refreshWith(
          { status: "published", deleteIndex: categoryIndex },
          { url: "back" }
        );
      }
    )
    .post(
      "/cycle/store/sort-category",
      // Reorder Position of Categories
      async ({ api, assert, flash, res, req }) => {
        const { body } = req;

        assert(
          body.sortedCategories,
          400,
          i18n.t("dashboard:cycleActions.provideAtLeastOneCategory")
        );

        await api(`cycle-sort-categories`, {
          method: "PUT",
          body,
        });

        flash("success", i18n.t("dashboard:cycleActions.successfullySorted"));
        res.refreshWith(
          { status: "published", "modal[type]": "", update: true },
          { url: "back", hash: "#_" }
        );
      }
    )

    .post(
      "/cycle/store/popular-program",
      async ({ api, assert, flash, res, req }) => {
        const { body } = req;

        assert(
          body.programId,
          400,
          i18n.t("dashboard:cycleActions.provideProgram")
        );

        await api(`cycle-categories-popular`, {
          method: "POST",
          body,
        });

        flash(
          "success",
          i18n.t("dashboard:cycleActions.programSetAsMostPopular")
        );

        res.refreshWith(
          { status: "published", "modal[type]": "", favorite: body.programId },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/edit/program-workout-name/:cycleId",
      async ({ api, res, req, load, locals, flash, session }) => {
        const body = req.body;
        const cycleId = locals.params.cycleId;

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - WORKOUT NAME",
            "6906fa7a2467801a6168cb99",
            body
          );
        }

        const sessionOrder = req.query.sessionOrder;
        const sessionWeek = req.query.sessionWeek;
        const { updatedWorkoutName = "" } = body;

        let queryEditCycle = {
          cycle: cycleId,
          proxyAthlete: req.query.athlete,
        };

        if (req.query.athlete && locals.activeUser.organizationId) {
          queryEditCycle = {
            ...queryEditCycle,
            athleteFromOrganization: req.query.athlete,
          };
        }

        await load("editCycle", queryEditCycle);

        const program = locals.editCycle?.programs[locals.query.program - 1];

        if (cycleId === "6906fa7a2467801a6168cb99") {
          console.log(
            "LUISLOG - WORKOUT NAME",
            "6906fa7a2467801a6168cb99",
            program
          );
        }

        const sessions = program && program.sessions ? program.sessions : [];
        const sessionsToUpdate = [...sessions];

        const indexToUpdate = sessionsToUpdate.findIndex(
          (item) => item.order === sessionOrder && item.week === sessionWeek
        );

        if (indexToUpdate >= 0 && indexToUpdate < sessionsToUpdate.length) {
          sessionsToUpdate[indexToUpdate] = {
            ...sessionsToUpdate[indexToUpdate],
            workoutName: updatedWorkoutName,
          };

          locals.editCycle.programs[locals.query.program - 1] = Object.assign(
            locals.editCycle.programs[locals.query.program - 1],
            { sessions: sessionsToUpdate },
            { modifiedDate: Date.now() }
          );

          locals.editCycle.programs.forEach((program) => {
            if ("undefined" in program) delete program["undefined"];
          });

          try {
            if (cycleId === "6906fa7a2467801a6168cb99") {
              console.log(
                "LUISLOG - WORKOUT NAME",
                "6906fa7a2467801a6168cb99",
                locals.editCycle.programs
              );
            }

            const response = await api(`cycle/${cycleId}`, {
              method: "PUT",
              body: {
                programs: locals.editCycle.programs,
                modifiedDate: Date.now(),
              },
            });
            const cycle = await response.json();
            setSession(session, "editCycle", cycle);
          } catch (e) {
            console.log("Error editing the workout name: ", e);
          }
        }

        flash("success", i18n.t("dashboard:cycleActions.successfullyUpdated"));
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .post(
      "/cycle/edit/program-workout-day/:cycleId",
      async ({ api, res, req, locals, flash, session }) => {
        const body = req.body;

        if (locals.params.cycleId === "6906fa7a2467801a6168cb99") {
          console.log("LUISLOG - WORKOUT DAY", "6906fa7a2467801a6168cb99", {
            body,
          });
        }

        const cycleId = locals.params.cycleId;
        const {
          programOrder,
          sessionOrder,
          switching,
          sessionOrderSwitch,
          sessionOrderSwitchValue,
        } = body;
        const { workoutDay = 0 } = body;

        try {
          const res = await api(`cycle-teams-workout-day`, {
            method: "PUT",
            body: {
              cycleId,
              programOrder,
              sessionOrder,
              workoutDay,
              switching,
              sessionOrderSwitch,
              sessionOrderSwitchValue,
            },
          });
          const response = await res.json();
          const { cycle } = response;

          locals.editCycle = cycle;
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyUpdated")
          );
        } catch (e) {
          console.log("Error editing the workout name: ", e);
          flash(
            "error",
            i18n.t("dashboard:cycleActions.errorUpdatingWorkoutDay")
          );
        }

        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .post(
      "/cycle/cycle-teams-end-program/:cycleId",
      // From the program editor footer, copy a week and add it to the end of a program
      async ({ api, res, req, flash }) => {
        const {
          params: { cycleId },
          body: { fromTemplates },
        } = req;

        await api("cycle-teams-end-program", {
          method: "PUT",
          body: { cycleId },
        });

        flash("success", i18n.t("dashboard:cycleActions.teamEnded"));

        res.redirect(
          fromTemplates
            ? `/coaching-portal/all-templates?owner=coach&status=draft&tabView=teams-programs-tab&templateView=teams-programs`
            : `/coaching-portal/store/?storeType=teams&status=draft`
        );
      }
    )
    .post(
      "/cycle/teams-publish/:id",
      async ({ api, assert, res, locals, flash, req }) => {
        const { id } = locals.params;
        const body = req.body;

        assert(id, 400, i18n.t("dashboard:cycleActions.programIdRequired"));

        // I love making 3 api calls in a single submit
        try {
          await api(`cycle-publish`, {
            method: "PUT",
            body: { id },
          });

          // Will always be true for people who already have community chat on (hopefully)
          if (body.enableChat) {
            const { pushAccountInfo, pressAccountInfo, pullAccountInfo } =
              locals?.activeUserToRequest;

            const isCommunityChatEnabled =
              pushAccountInfo?.inAppCommunityEnabled ||
              pressAccountInfo?.inAppCommunityEnabled ||
              pullAccountInfo?.inAppCommunityEnabled;

            if (!isCommunityChatEnabled) {
              const { activeUserToRequest } = locals;
              const { _id: userId } = activeUserToRequest;

              const enableBody = {};
              if (pushAccountInfo) {
                enableBody.pushAccountInfo = {
                  ...pushAccountInfo,
                  inAppCommunityEnabled: true,
                };
              }
              if (pullAccountInfo) {
                enableBody.pullAccountInfo = {
                  ...pullAccountInfo,
                  inAppCommunityEnabled: true,
                };
              }

              if (Object.keys(enableBody).length > 0) {
                await api(`user/${userId}`, {
                  method: "PUT",
                  body: enableBody,
                });
              }
            }

            const { activeUser } = locals;
            const { guests, subject } = body;

            // Determine types based on enabled communities
            const pushEnabled =
              pushAccountInfo?.inAppCommunityEnabled ||
              (body.enableChat && !!pushAccountInfo);
            const pressEnabled = pressAccountInfo?.inAppCommunityEnabled;
            const pullEnabled =
              pullAccountInfo?.inAppCommunityEnabled ||
              (body.enableChat && !!pullAccountInfo && !pushAccountInfo);
            const types = [];
            if (pushEnabled) types.push("push");
            if (pressEnabled) types.push("press");
            if (pullEnabled) types.push("pull");

            const bodyToSend = {
              guests,
              subject,
              ownerId: String(activeUser._id),
              teamId: String(id),
              types,
            };

            await api(`create-chat-channel`, {
              method: "POST",
              body: bodyToSend,
            });
          }

          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyPublished")
          );
          res.refreshWith(
            { "modal[type]": "team-published", status: "published" },
            { url: "back" }
          );
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({ status: "draft" }, { url: "back" });
        }
      }
    )
    .get(
      "/cycle/teams-unpublish/:id",
      async ({ api, assert, res, locals, flash }) => {
        let { id } = locals.params;
        assert(id, 400, i18n.t("dashboard:cycleActions.programIdRequired"));

        try {
          await api(`cycle-teams-unpublish`, {
            method: "PUT",
            body: { id },
          });
        } catch (e) {
          console.log("Error", e);
        }
        flash(
          "success",
          i18n.t("dashboard:cycleActions.successfullyUnpublished")
        );
        // res.redirect(`/coaching-portal/store/teams/${id}`);
        res.refreshWith(
          { "modal[type]": "", status: "draft" },
          { url: "back" }
        );
      }
    )
    .get(
      "/cycle/teams-remove-member/:cycleId/:memberId",
      async ({ api, assert, res, locals, flash }) => {
        let { cycleId, memberId } = locals.params;
        assert(
          cycleId,
          400,
          i18n.t("dashboard:cycleActions.programIdRequired")
        );

        try {
          await api(`cycle-teams-remove-member`, {
            method: "PUT",
            body: { cycleId, memberId },
          });
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyRemovedMember")
          );
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("dashboard:cycleActions.errorRemovingMember"));
        }

        res.refreshWith(
          { "modal[type]": "", currentTab: "members" },
          { url: "back" }
        );
      }
    )
    .post(
      "/cycle/:cycleId/teams-add-week",
      async ({ api, assert, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { fromTemplates } = req.body;

        assert(cycleId, 400, i18n.t("dashboard:cycleActions.teamIdRequired"));

        try {
          const updatedCycle = await api(`cycle-teams-add-week`, {
            method: "PUT",
            body: { cycleId },
          });
          const response = await updatedCycle.json();
          const { cycle } = response;

          locals.editCycle = cycle;

          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyAddedNewWeek")
          );

          res.redirect(
            `/coaching-portal/${fromTemplates ? "templates" : "store"}/edit-program/${cycleId}?program=${cycle.programs.length}&storeType=teams`
          );
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("dashboard:cycleActions.errorAddingWeek"));
        }
      }
    )
    .post(
      // Two segments on purpose: `.post("/cycle/:id")` is registered earlier
      // and would otherwise match this as a cycle id. Same shape as the
      // existing "/cycle/program/addWeek" and "/cycle/store/reorder".
      "/cycle/flexible/start",
      async ({ api, res, req, locals, flash }) => {
        const { activeUserToRequest, activeUser } = locals;
        const { files } = req;
        const {
          clientId,
          name,
          description,
          sessionsPerWeek,
          startDate,
          firstDayOfWeek,
          trainingSplitType,
          confirmMove,
          organizationCoach,
        } = req.body;

        if (!clientId) {
          flash("error", i18n.t("athlete-portal:flexible.errors.noClient"));
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
          return;
        }

        try {
          const response = await api("flexible-program-start", {
            method: "POST",
            body: {
              clientId,
              ...(name ? { name } : {}),
              ...(description ? { description } : {}),
              ...(sessionsPerWeek
                ? { sessionsPerWeek: Number(sessionsPerWeek) }
                : {}),
              // startDate wins server-side regardless (see
              // flexible-program-start's own doc comment) - firstDayOfWeek is
              // only sent as a fallback for whatever it's still worth.
              // DateInput's native form value is display-formatted ("MMM D,
              // YYYY"), same as every other date field submitted through a
              // real multipart form in this file - normalise it the same way.
              ...(startDate
                ? { startDate: moment(startDate).format("YYYY-MM-DD") }
                : {}),
              ...(firstDayOfWeek !== undefined && firstDayOfWeek !== ""
                ? { firstDayOfWeek: Number(firstDayOfWeek) }
                : {}),
              ...(trainingSplitType ? { trainingSplitType } : {}),
              confirmMove: confirmMove === true || confirmMove === "true",
              firebaseProjectId: activeUserToRequest.firebaseProjectId
                ? activeUserToRequest.firebaseProjectId
                : defaultFirebaseProjectId,
              avatar: activeUser?.avatar?.file ? activeUser.avatar.file : null,
            },
            files,
          });

          const data = await response.json();
          // The API reports failures in the body, not by throwing.
          if (!data?.success) {
            throw new Error(
              data?.error || i18n.t("athlete-portal:flexible.errors.start")
            );
          }

          flash("success", i18n.t("athlete-portal:flexible.started"));
          res.redirect(
            `/athlete-portal/programs/flexible-program/${data.cycle._id}?athlete=${clientId}${
              organizationCoach ? `&organizationCoach=${organizationCoach}` : ""
            }`
          );
        } catch (e) {
          console.log("Error", e);
          flash(
            "error",
            e.message || i18n.t("athlete-portal:flexible.errors.start")
          );
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
        }
      }
    )
    .post(
      "/cycle/:cycleId/flexible-add-week",
      async ({ api, assert, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { startDate, today, athlete } = req.body;

        assert(cycleId, 400, i18n.t("dashboard:cycleActions.teamIdRequired"));

        try {
          // Same endpoint as teams, but for flexible it stamps the next
          // lattice boundary rather than max(startDate) + 7.
          const updatedCycle = await api("cycle-teams-add-week", {
            method: "PUT",
            body: {
              cycleId,
              ...(startDate ? { startDate: Number(startDate) } : {}),
              ...(today ? { today: Number(today) } : {}),
            },
          });
          const { cycle } = await updatedCycle.json();

          locals.editCycle = cycle;
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyAddedNewWeek")
          );

          res.redirect(
            `/athlete-portal/programs/edit-program/${cycleId}?program=${cycle.programs.length}&storeType=flexible${athlete ? `&athlete=${athlete}` : ""}`
          );
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("dashboard:cycleActions.errorAddingWeek"));
        }
      }
    )
    .post(
      "/cycle/:cycleId/flexible-settings",
      async ({ api, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { files } = req;
        const {
          name,
          description,
          video,
          sessionsPerWeek,
          trainingSplitType,
          previousTrainingSplitType,
        } = req.body;

        try {
          const body = {};
          // Name is never intentionally blanked from this form (the field is
          // required in the UI); description/video ARE meant to be clearable.
          if (name !== undefined && name !== "") body.name = name;
          if (description !== undefined) body.description = description;
          if (video !== undefined) body.video = video;
          if (sessionsPerWeek !== undefined) {
            // Only the NEXT authored week reads this (cycle-teams-add-week
            // stamps it in when the coach adds a week) - the live week keeps
            // whatever session count it was created with.
            body.sessionsPerWeek = Number(sessionsPerWeek);
          }

          if (Object.keys(body).length || files?.image) {
            await api(`cycle/${cycleId}`, {
              method: "PUT",
              body: { ...body, _id: cycleId },
              files,
            });
          }

          // The split has its own endpoint because it also has to be stamped
          // onto the live authored week, not just the cycle.
          if (
            trainingSplitType &&
            trainingSplitType !== previousTrainingSplitType
          ) {
            await api("cycle-teams-training-split", {
              method: "PUT",
              body: { cycleId, trainingSplitType },
            });
          }

          flash("success", i18n.t("athlete-portal:flexible.updated"));
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("athlete-portal:flexible.errors.update"));
        }
      }
    )
    .get(
      // ToggleLinksInput (the segmented control) only ever navigates via a
      // plain <a href>, so the split change needs a GET action - same idiom
      // as cycle-teams-unpublish above. trainingSplitType still also updates
      // through flexible-settings for callers that already have a body.
      "/cycle/:cycleId/flexible-split/:trainingSplitType",
      async ({ api, res, locals, flash }) => {
        const { cycleId, trainingSplitType } = locals.params;

        try {
          await api("cycle-teams-training-split", {
            method: "PUT",
            body: { cycleId, trainingSplitType },
          });
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("athlete-portal:flexible.errors.update"));
        }

        res.refreshWith({}, { url: "back" });
      }
    )
    .post(
      // Teams' own "/cycle/program/duplicate/:cycleId" is a plain GET link
      // with no body - it can't carry the coach-picked startDate a repeating
      // flexible week needs, so this is its own POST rather than a shared
      // route with a flexible-only field bolted on.
      "/cycle/:cycleId/flexible-duplicate-week",
      async ({ api, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { programOrder, startDate, today, athlete } = req.body;

        try {
          const response = await api("cycle-duplicate-program", {
            method: "POST",
            body: {
              cycle: cycleId,
              program: Number(programOrder),
              ...(startDate ? { startDate: Number(startDate) } : {}),
              ...(today ? { today: Number(today) } : {}),
            },
          });
          const data = await response.json();
          if (!data?.success) {
            throw new Error(
              data?.error || i18n.t("athlete-portal:flexible.errors.update")
            );
          }
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("athlete-portal:flexible.errors.update"));
        }

        res.redirect(
          `/athlete-portal/programs/flexible-program/${cycleId}${athlete ? `?athlete=${athlete}` : ""}`
        );
      }
    )
    .post(
      // The modal has already shown the overwrite warning by the time this
      // fires, so the request always confirms; the API still refuses a start
      // week that has already begun.
      "/cycle/:cycleId/flexible-import-template",
      async ({ api, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { template, startDate, today, athlete } = req.body;

        try {
          const response = await api("flexible-import-template", {
            method: "POST",
            body: {
              cycle: cycleId,
              template,
              startDate: Number(startDate),
              ...(today ? { today: Number(today) } : {}),
              confirm: true,
            },
          });
          const data = await response.json().catch(() => null);
          if (!data?.success) {
            // The modal now calls the API itself and renders `invalid` /
            // `blocked` inline; this keeps the details if anything still
            // posts here.
            const details = [
              ...(data?.invalid || []).map((item) =>
                item.reason === "emptyWeek"
                  ? `${item.blockName || item.blockOrder} · Week ${item.week}: no workouts`
                  : `${item.blockName || item.blockOrder} · Week ${item.week} · ${item.sessionName || item.sessionOrder}: no exercises`
              ),
              ...(data?.blocked || []).map(
                (week) => `${week.name || week.startDate}: logged training`
              ),
            ];
            throw new Error(
              [
                data?.error?.message ||
                  data?.error ||
                  i18n.t("athlete-portal:flexible.errors.update"),
                ...details,
              ].join(" ")
            );
          }
        } catch (e) {
          console.log("Error", e);
          flash(
            "error",
            e.message || i18n.t("athlete-portal:flexible.errors.update")
          );
        }

        res.redirect(
          `/athlete-portal/programs/flexible-program/${cycleId}${athlete ? `?athlete=${athlete}` : ""}`
        );
      }
    )
    .post(
      // Fired from a plain button (not a link) on each week row, so it's a
      // POST like flexible-settings rather than the GET split-switcher above.
      "/cycle/:cycleId/flexible-week-repeat",
      async ({ api, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { programOrder, repeatActiveWeek } = req.body;

        try {
          await api("flexible-week-repeat", {
            method: "PUT",
            body: {
              cycleId,
              programOrder: Number(programOrder),
              repeatActiveWeek:
                repeatActiveWeek === true || repeatActiveWeek === "true",
            },
          });
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("athlete-portal:flexible.errors.update"));
        }

        res.refreshWith({}, { url: "back" });
      }
    )
    .post(
      "/cycle/:cycleId/flexible-stop",
      async ({ api, res, req, locals, flash }) => {
        const { cycleId } = locals.params;
        const { athlete } = req.body;

        try {
          // Reuses the teams endpoint: it asserts the caller is in
          // cycle.members, which flexible-program-start populates with the one
          // client, and proxyAthlete makes ctx.user that client.
          await api("cycle-teams-stop-program", {
            method: "PUT",
            body: { cycleId, type: "teams", proxyAthlete: athlete },
          });

          flash("success", i18n.t("athlete-portal:flexible.stopped"));
          res.redirect(
            `/athlete-portal/programs${athlete ? `?athlete=${athlete}` : ""}`
          );
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("athlete-portal:flexible.errors.stop"));
        }
      }
    )
    .post(
      "/cycle/create-chat/:cycleId",
      async ({ api, assert, res, locals, flash, req }) => {
        const { cycleId } = locals.params;
        const { body } = req;

        assert(cycleId, 400, i18n.t("dashboard:cycleActions.teamIdRequired"));

        try {
          const { activeUserToRequest } = locals;
          const { pushAccountInfo, pressAccountInfo, pullAccountInfo } =
            activeUserToRequest;
          const { guests, subject } = body;

          // Determine types based on enabled communities
          const pushEnabled = pushAccountInfo?.inAppCommunityEnabled;
          const pressEnabled = pressAccountInfo?.inAppCommunityEnabled;
          const pullEnabled = pullAccountInfo?.inAppCommunityEnabled;
          const types = [];
          if (pushEnabled) types.push("push");
          if (pressEnabled) types.push("press");
          if (pullEnabled) types.push("pull");

          const bodyToSend = {
            guests,
            subject,
            ownerId: String(activeUserToRequest._id),
            teamId: String(cycleId),
            types,
          };

          await api(`create-chat-channel`, {
            method: "POST",
            body: bodyToSend,
          });
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyCreatedChat")
          );

          res.redirect(
            `/coaching-portal/store/teams/${cycleId}?currentTab=manager`
          );
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("dashboard:cycleActions.errorCreatingChat"));
        }
      }
    )
    .post(
      "/cycle/add-pull-members-to-team-chat/:cycleId",
      async ({ api, assert, res, locals, flash, req }) => {
        const { cycleId } = locals.params;
        const { memberId } = req.body || {};

        assert(cycleId, 400, i18n.t("dashboard:cycleActions.teamIdRequired"));

        try {
          await api("add-pull-members-to-team-chat", {
            method: "POST",
            body: {
              teamId: String(cycleId),
              ...(memberId && { memberId: String(memberId) }),
            },
          });
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyInvitedPullMembers")
          );
        } catch (e) {
          flash("error", e.message);
        }

        res.redirect(
          `/coaching-portal/store/teams/${cycleId}?currentTab=members`
        );
      }
    )
    .post(
      "/cycle/teams-duplicate-week/:cycle",
      async ({ api, assert, res, locals, flash, req }) => {
        const { cycle } = locals.params;
        const { body } = req;
        const { program, clonedProgram } = body;

        assert(cycle, 400, i18n.t("dashboard:cycleActions.teamIdRequired"));

        try {
          const response = await api(`cycle-teams-duplicate-week`, {
            method: "POST",
            body: {
              cycle,
              program,
              clonedProgram,
            },
          });
          const data = await response.json();

          if (cycle === "6906fa7a2467801a6168cb99") {
            console.log(
              "LUISLOG - DUPLICATE WEEK",
              "6906fa7a2467801a6168cb99",
              {
                editCycle: locals.editCycle,
                cycle: data.cycle,
              }
            );
          }

          locals.editCycle = data.cycle;

          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyDuplicated")
          );
          res.refreshWith({}, { url: "back" });
        } catch (e) {
          console.log("Error", e);
          flash("error", i18n.t("dashboard:cycleActions.errorCreatingChat"));
        }
      }
    )
    .post(
      "/cycle/teams-invite-athletes/:cycleId",
      async ({ api, res, flash, req, locals }) => {
        const { activeUserToRequest, activeUser } = locals;
        const { params, body } = req;
        const { cycleId } = params;
        const { recipients, program, confirmMove } = body;

        const recipientList = Array.isArray(recipients)
          ? recipients
          : typeof recipients === "string" && recipients
            ? recipients
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean)
            : [];

        if (recipientList.length === 0) {
          flash("error", "No recipients selected");
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
          return;
        }

        const doInvite = async () => {
          await api(`cycle-teams-invite-members`, {
            method: "POST",
            body: {
              recipients: recipientList,
              cycle: cycleId,
              programOrder: program,
              firebaseProjectId: !!activeUserToRequest.firebaseProjectId
                ? activeUserToRequest.firebaseProjectId
                : defaultFirebaseProjectId,
              avatar:
                !!activeUser.avatar && !!activeUser.avatar.file
                  ? activeUser.avatar.file
                  : null,
            },
          });
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyInvitedAthletes")
          );
          res.refreshWith(
            {
              "modal[type]": "",
              recipients: "",
              inTeamNames: "",
              flexibleProgramNames: "",
              pendingInvite: "",
              program: "",
            },
            { url: "back" }
          );
        };

        try {
          if (confirmMove) {
            await doInvite();
            return;
          }

          const checkRes = await api("cycle-teams-check-members", {
            method: "POST",
            body: { recipientIds: recipientList },
          });
          const checkData = await checkRes.json();
          const inTeam = checkData?.inTeam ?? [];
          const onFlexibleProgram = checkData?.onFlexibleProgram ?? [];

          if (inTeam.length === 0 && onFlexibleProgram.length === 0) {
            await doInvite();
          } else {
            const inTeamNames = inTeam
              .map((a) => a.name || "Unknown")
              .join(",");
            const flexibleProgramNames = onFlexibleProgram
              .map((a) => a.name || "Unknown")
              .join(",");
            res.refreshWith(
              {
                "modal[type]": "team-invite-clients",
                pendingInvite: "1",
                recipients: recipientList.join(","),
                program: program ?? "",
                inTeamNames,
                flexibleProgramNames,
              },
              { url: "back" }
            );
          }
        } catch (e) {
          console.log("Error", e);
          flash(
            "error",
            i18n.t("dashboard:cycleActions.errorInvitingAthletes")
          );
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
        }
      }
    )
    .post(
      "/cycle/assign-single-workout-to-athletes/:cycleId",
      async ({ api, res, flash, req, locals }) => {
        const { activeUserToRequest, activeUser } = locals;
        const { params, body } = req;
        const { cycleId } = params;
        const { recipients, date } = body;

        const recipientList = Array.isArray(recipients)
          ? recipients
          : typeof recipients === "string" && recipients
            ? recipients
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean)
            : [];

        if (recipientList.length === 0) {
          flash("error", "No recipients selected");
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
          return;
        }

        if (!date) {
          flash("error", "No date selected");
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
          return;
        }

        try {
          await api(`cycle-assign-single-workout-to-members`, {
            method: "POST",
            body: {
              recipients: recipientList,
              cycle: cycleId,
              date,
              firebaseProjectId:
                activeUserToRequest.firebaseProjectId ||
                defaultFirebaseProjectId,
              avatar: activeUser.avatar?.file ?? null,
            },
          });
          flash(
            "success",
            i18n.t("dashboard:cycleActions.successfullyAddedWorkout")
          );
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
        } catch (e) {
          console.log("Error", e);
          flash(
            "error",
            i18n.t("dashboard:cycleActions.errorAddingWorkout")
          );
          res.refreshWith({ "modal[type]": "" }, { url: "back" });
        }
      }
    );
};

export default Cycle;
